import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Button, Input, Label, Row, Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, groupBy } from 'lodash-es';

import ConfirmModal from '../../../utilities/form-components/ConfirmModal';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import Step4File from './Step4File';
import imageUploadErrors from './imageUploadErrors';
import ImageSizeTestBanner from '../ImageSizeTestBanner';

/**
 * This container represents everything underneath the dropzone.  Note that the
 * functionality is still handled at the parent level.
 */
class Step4FileContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: props.files,
      showModal: false,
      showWarningBanner: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    // The user uploaded more files or deleted some
    const stateObj = {};
    if (
      props.files.length !== state.files.length ||
      props.files.some(({ isUpdatedFile }) => isUpdatedFile)
    ) {
      stateObj.files = props.files;
    }

    return stateObj;
  }

  toggleDeleteImageModal = () => {
    this.setState({ showModal: !this.state.showModal });
  };

  handleImageSizeCheck = (imageCheck) => {
    if (imageCheck) {
      this.setState({ showWarningBanner: true });
    }
  };

  render() {
    const { files, showModal, showWarningBanner } = this.state;

    if (isEmpty(files)) {
      return null;
    }

    // DY Removed handleSelectAll, handleSelectFile, isUploading
    const {
      abortFile,
      addFileError,
      catalogID,
      deleteFilesWithErrors,
      handleDeleteFile,
      handleFileComplete,
      fileUploadFormat,
      filterOnlyErrors,
      handleErrorFilter,
      house: { houseID },
      inventoryNumbers,
      lotNumbers,
      numFilesComplete,
      numFilesTotal,
      type,
      updateFileByIndex,
      isUpdatedFileHandler
    } = this.props;

    // Boolean to indicate which checkbox icon to show
    // DY - May re-add in the future
    // const areFilesSelected = files.some(file =>
    //   filterOnlyErrors ? file.isSelected && file.hasError : file.isSelected
    // );

    // Parse file information for progress bar
    const overallProgress = Math.round((numFilesComplete / numFilesTotal) * 100);

    // Parse file information for error details
    const fileErrors = files.filter(file => file.hasError);
    const numFilesErrors = fileErrors.length;

    let errorSummary = null;
    let errorGroups = null;
    if (numFilesErrors > 0) {
      // Create error summary
      const baseText =
        'The following images have errors. Please note that any images with errors will not be saved:';
      let secondaryText = '';
      fileErrors.forEach(file => {
        secondaryText += `${file.name}, `;
      });
      secondaryText = secondaryText.slice(0, -2);

      errorSummary = (
        <InlineAlert
          showPhones={false}
          titleText={`Error Summary: Success: ${numFilesComplete -
            numFilesErrors}, Error: ${numFilesErrors}, Pending: ${numFilesTotal -
            numFilesComplete}, Total: ${numFilesTotal}`}
          baseText={baseText}
          secondaryText={secondaryText}
        />
      );

      // For each kind of error - we want to show a banner and list all files with that error
      errorGroups = groupBy(fileErrors, file => file.error.code);
    }

    // Row with columns for each file
    return (
      <div className="mt-4">
        <p className="mb-2">
          Overall Upload Progress: {overallProgress}% ({numFilesComplete} of {numFilesTotal})
        </p>
        <Progress className="mb-2 w-50" value={overallProgress} />

        {/* Error Summary */}
        {errorSummary}

        {/* Error Banners for each type of error */}
        {errorGroups &&
          Object.keys(errorGroups).map(errorCode => {
            const { title, summary } = imageUploadErrors(errorCode, type);

            let secondaryText = '';
            errorGroups[errorCode].forEach(file => {
              secondaryText += `${file.name}, `;
            });
            secondaryText = secondaryText.slice(0, -2);

            return (
              <InlineAlert
                key={errorCode}
                showPhones={false}
                titleText={title}
                baseText={summary}
                secondaryText={secondaryText}
              />
            );
          })}

        <div>
          {/* DY - we may readd this functionality */}
          {/*
          {areFilesSelected ? (
            <FontAwesomeIcon
              className={
                isUploading
                  ? 'color-blue cursor-not-allowed mr-1'
                  : 'color-blue cursor-pointer mr-1'
              }
              icon="minus-square"
              onClick={!isUploading ? handleSelectAll : null}
            />
          ) : (
            <Input
              id="selectAllFiles"
              className={
                isUploading
                  ? 'ml-0 mr-1 position-relative cursor-not-allowed'
                  : 'ml-0 mr-1 position-relative cursor-pointer'
              }
              checked={false}
              disabled={isUploading}
              onChange={handleSelectAll}
              type="checkbox"
            />
          )}
          <Label className="mr-4" for="selectAllFiles">
            Select All Files
          </Label>
          */}

          {/* DY - Previous iteration was disabled if no errors */}
          {numFilesErrors > 0 && (
            <Fragment>
              <Input
                id="viewFileErrors"
                className="ml-0 mr-1 position-relative cursor-pointer"
                checked={filterOnlyErrors}
                onChange={handleErrorFilter}
                type="checkbox"
              />
              {/* DY - Previous iteration had text muted if no errors */}
              <Label for="viewFileErrors" className="mr-4 mt-0 mb-3">
                View items with errors
              </Label>
            </Fragment>
          )}

          {/* DY - Commented out */}
          {/* onClick={areFilesSelected ? () => this.toggleDeleteImageModal() : null}> */}
          {numFilesErrors > 0 && (
            <Button color="link" onClick={() => this.toggleDeleteImageModal()}>
              <FontAwesomeIcon className="color-blue cursor-pointer mr-1" icon="trash-alt" />
              Delete items with errors
            </Button>
          )}
        </div>

        <ConfirmModal
          actionFunction={deleteFilesWithErrors}
          bodyText="Are you sure you want to delete all files with errors?"
          buttonText="Delete"
          showModal={showModal}
          title="Delete Items"
          toggleFunction={this.toggleDeleteImageModal}
        />

        {showWarningBanner && (
          <Row>
            <ImageSizeTestBanner />
          </Row>
        )}

        <Row>
          {files.map((file, index) => (
            <Step4File
              key={file.name}
              abortFile={abortFile}
              addFileError={addFileError}
              catalogID={catalogID}
              file={file}
              filterOnlyErrors={filterOnlyErrors}
              fileUploadFormat={fileUploadFormat}
              handleDeleteFile={handleDeleteFile}
              handleFileComplete={handleFileComplete}
              houseID={houseID}
              index={index}
              inventoryNumbers={inventoryNumbers}
              lotNumbers={lotNumbers}
              handleImageSizeCheck={this.handleImageSizeCheck}
              updateFileByIndex={updateFileByIndex}
              isUpdatedFileHandler={isUpdatedFileHandler}
            />
          ))}
        </Row>
      </div>
    );
  }
}

const mapStateToProps = ({ house }) => ({
  house
});

export default connect(mapStateToProps, null)(Step4FileContainer);
